import {Trans} from '@lingui/macro';
import tagifyCss from '@yaireo/tagify/dist/tagify.css';
import {isUndefined} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useCss} from '../../../../libs/shared/ui/src/css/cssContext';
import {useNavigationBlocking} from '../../../../srcCommon/app/navigationBlocking';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {PreviewerModel, UnsavedPreviewerModel} from '../../../models/api/previewerModel';
import {SettingsResourcePagesEnum} from '../../../models/url/settingsUrlModel';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {CustomFields} from '../../editors/commons/customFieldsModel';
import {StyledEditorMain, StyledEditorWrapper} from '../../editors/commons/editorCommons';
import {ExpressionCreationState} from '../../editors/expressions/expressionsContext';
import {ExpressionsSidebar} from '../../editors/expressions/expressionsSidebar';
import {ExpressionsSidePanel} from '../../editors/expressions/expressionsSidePanel';
import {
  createPreviewerEditorContext,
  PreviewerEditorContextProvider,
} from '../../editors/previewerEditor/previewerEditorContext';
import {PreviewerEditorState} from '../../editors/previewerEditor/previewerEditorState';
import {usePreviewerEditorState} from '../../editors/previewerEditor/usePreviewerEditorState';
import {DiscardChangesDialog} from '../../rules/ruleEditor/dialog/discardChangesDialog';
import {ResourceEditorFooter} from '../commons/resourceEditorFooter';
import {PreviewerDescriptionInput} from './inputs/previewerDescriptionInput';
import {PreviewerDomainInput} from './inputs/previewerDomainInput';
import {PreviewerTestDataSidebar} from './inputs/PreviewerTestDataSidebar';
import {previewerEditorFormErrorLabels} from './previewerEditorFormErrors';
import {PreviewerBuilderSection} from './sections/previewerBuilderSection';
import {PreviewerConditionsSectionWithIntegration} from './sections/previewerConditionsSection';

/*
 * Props.
 */

interface PreviewerEditorWrapperProps {
  panelId: SettingsPanelId;

  previewer: PreviewerModel | undefined;
  application: LocalApplicationModel;
  customFields: CustomFields;

  isReadOnly?: boolean;

  onSavePreviewer?: HandlerOf<UnsavedPreviewerModel>;

  navigateTo: (resourcePage: SettingsResourcePagesEnum, id?: number) => void;
}

/*
 * Styles.
 */

export const StyledPreviewerSidebarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  column-gap: 20px;
`;

/*
 * Component.
 */

export const PreviewerEditorWrapper: FC<PreviewerEditorWrapperProps> = (props) => {
  const {panelId, previewer, application, customFields, isReadOnly, onSavePreviewer, navigateTo} = props;

  // Enable tagify styles.
  useCss(tagifyCss);

  const initialEditorState = useMemo(
    () => PreviewerEditorState.createInitialState(previewer, customFields),
    [previewer, customFields],
  );

  const {previewerEditorState, ...actions} = usePreviewerEditorState(previewer, customFields, application);
  const {previewerState} = previewerEditorState;

  const hasDiff = PreviewerEditorState.hasDiff(initialEditorState, previewerEditorState);

  // Prevent the user from navigating away when there are unsaved changes.
  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(panelId, hasDiff);

  const formErrors = PreviewerEditorState.computeFormErrors(previewerEditorState);

  const [expressionCreationState, setExpressionCreationState] = useState<ExpressionCreationState>();

  const editorContext = createPreviewerEditorContext({
    namespace: 'global',
    isEditing: !isUndefined(previewer),
    isReadOnly,
    hasExpressions: true,
    hasHighlightErrorsFlag: false,
    expressions: previewerEditorState.expressions,
    customFields,
    application,
    setPanelOpen: () => actions.onSetPanelOpen(true),
    expressionCreationState,
    setExpressionCreationState,
  });

  const onSaveButtonClick = () => {
    if (isReadOnly || !onSavePreviewer) {
      return;
    }

    const updatedPreviewer = PreviewerEditorState.toPreviewer(previewerEditorState);

    // Save autolinker.
    onSavePreviewer(updatedPreviewer);

    // Unblock navigation.
    unblockNavigation();

    // Navigate back to the autolinkers list.
    navigateTo(SettingsResourcePagesEnum.OVERVIEW);
  };

  return (
    <PreviewerEditorContextProvider context={editorContext}>
      <StyledEditorMain>
        <StyledEditorWrapper>
          <PreviewerDomainInput
            domain={previewerState.domain}
            onUpdate={actions.onUpdateDomain}
            isReadOnly={editorContext.isReadOnly}
          />
          <PreviewerDescriptionInput
            description={previewerState.description}
            onUpdate={actions.onUpdateDescription}
            isReadOnly={editorContext.isReadOnly}
          />
          <PreviewerConditionsSectionWithIntegration
            conditions={previewerEditorState.previewerState.conditions}
            onAddCondition={actions.onAddCondition}
            onUpdateCondition={actions.onUpdateCondition}
            onDeleteCondition={actions.onDeleteCondition}
          />
          <PreviewerBuilderSection
            expressions={previewerEditorState.expressions}
            linkType={previewerState.linkType}
            onUpdateLinkType={actions.onUpdateLinkType}
            previewBuilder={previewerState.previewBuilder}
            onUpdatePreviewBuilder={actions.onUpdatePreviewBuilder}
            isReadOnly={editorContext.isReadOnly}
          />
        </StyledEditorWrapper>
        <StyledPreviewerSidebarWrapper>
          <ExpressionsSidebar
            state={previewerEditorState}
            onSelectExpression={actions.onSelectExpression}
            onCreateClick={() => actions.onSetPanelOpen(true)}
            isReadOnly={editorContext.isReadOnly}
          />
          <PreviewerTestDataSidebar
            previewerEditorState={previewerEditorState}
            actions={actions}
            isReadOnly={false}
          />
        </StyledPreviewerSidebarWrapper>
        {didBlockNavigation ? (
          <DiscardChangesDialog onCancel={cancelNavigation} onDiscard={unblockNavigation} />
        ) : null}
      </StyledEditorMain>
      <ResourceEditorFooter
        isEditing={editorContext.isEditing}
        isReadOnly={editorContext.isReadOnly}
        formErrors={formErrors}
        errorLabels={previewerEditorFormErrorLabels}
        onCancel={() => navigateTo(SettingsResourcePagesEnum.OVERVIEW)}
        onSave={onSaveButtonClick}
        createLabel={<Trans id="previewer-editor-footer-create">Create previewer</Trans>}
      />
      <ExpressionsSidePanel state={previewerEditorState} actions={actions} expressionsDependencies={{}} />
    </PreviewerEditorContextProvider>
  );
};
